<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :title="title || $t('common.works')"
    width="85%"
    height="85%"
    :wrapper-attr="{ class: 'work-selector-popup' }"
    :show-close-button="true"
    @hiding="onClose"
  >
    <GridPanelHeader
      :grid-ref="$refs.workGrid"
      :title="''"
      :show-add="false"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
      :show-refresh="true"
      :no-bottom-margin="true"
      @refresh="refreshGrid"
    />
    <DxDataGrid
      ref="workGrid"
      :data-source="workDataSource"
      key-expr="uuid"
      :remote-operations="{ paging: true, filtering: true }"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :selected-row-keys="selectedWorkUuids"
      :element-attr="{ class: 'smagria-grid work_selector_grid' }"
      @editor-preparing="onEditorPreparing"
      @row-click="onRowClick"
    >
      <DxSelection :mode="multiselect ? 'multiple' : 'single'" show-check-boxes-mode="always" select-all-mode="page" />
      <DxFilterRow :visible="true" :show-operation-chooser="true" apply-filter="auto" />
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[15, 25, 100]"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxColumn
        data-field="parcel.name"
        :caption="$t('common.parcel')"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :min-width="120"
      />
      <DxColumn
        data-field="parcel.surface"
        :caption="$t('common.surface_ha')"
        data-type="number"
        :filter-operations="['contains']"
        :allow-sorting="false"
      />
      <DxColumn
        data-field="season.uuid"
        cell-template="seasonViewTemplate"
        :caption="$t('common.season')"
        :filter-operations="['=']"
        :allow-sorting="false"
        :calculateCellValue="getSeasonColumnText"
        :min-width="150"
      >
        <DxLookup :data-source="seasons" value-expr="uuid" display-expr="name" />
      </DxColumn>
      <template #seasonViewTemplate="{ data }">
        <span>{{ getSeasonColumnText(data.data.season?.uuid) }}</span>
      </template>
      <DxColumn
        data-field="culture.name"
        :caption="$t('common.culture')"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :min-width="120"
      />
      <DxColumn
        data-field="work_started_at"
        :caption="$t('common.work_date')"
        data-type="date"
        format="yyyy-MM-dd"
        :filter-operations="['=']"
        :allow-sorting="false"
        :min-width="120"
      />
      <DxColumn
        data-field="work.name"
        :caption="$t('common.work_name')"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :min-width="120"
      />
      <DxColumn data-field="machine.name" :caption="$t('common.tractor')" :filter-operations="['contains']" :allow-sorting="false" />
      <DxColumn data-field="machinery.name" :caption="$t('common.work_machine')" :filter-operations="['contains']" :allow-sorting="false" />
      <DxColumn data-field="person.name" :caption="$t('common.driver')" :filter-operations="['contains']" :allow-sorting="false" />
      <DxColumn
        data-field="net_surface_worked"
        :caption="$t('works.work_list.worked_area')"
        data-type="number"
        :filter-operations="['contains']"
        :allow-sorting="false"
      />
      <DxColumn
        data-field="fuel_consumption"
        :caption="$t('tools.import_validator.fuel_consumption')"
        data-type="number"
        :filter-operations="['contains']"
        :allow-sorting="false"
      />
    </DxDataGrid>
    <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: auto; padding-top: 16px">
      <SmagriaButton :label="$t('common.continue')" customClass="primary col-lg-2 apply-btn" :action="onContinue" />
    </div>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxPaging, DxPager, DxLookup } from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";

export default {
  name: "WorkSelector",
  components: { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxPaging, DxPager, DxLookup, DxPopup, GridPanelHeader, SmagriaButton },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: "" },
    multiselect: { type: Boolean, default: true },
    filterData: { type: Object, default: () => ({}) },
    selectedWorkUuids: { type: Array, default: () => [] },
    lockContextFilters: { type: Boolean, default: false },
  },
  data() {
    return {
      localSelectedWorkUuids: [...this.selectedWorkUuids],
      workDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const pageSize = loadOptions.take || 15;
          const response = await this.getWorks({
            ...this.filterData,
            page: Math.floor((loadOptions.skip || 0) / pageSize) + 1,
            limit: pageSize,
            filter: loadOptions.filter,
          });
          const works = (response?.data || []).map((work) => {
            const date = work.work_date ? new Date(`${work.work_date}T00:00:00`) : null;
            return {
              ...work,
              work_started_at: date && !Number.isNaN(date.getTime()) ? date : null,
            };
          });
          return { data: works, totalCount: response?.pagination?.total ?? works.length };
        },
      }),
    };
  },
  watch: {
    selectedWorkUuids: {
      deep: true,
      handler(newData) {
        this.localSelectedWorkUuids = [...newData];
        this.$nextTick(() => this.$refs.workGrid?.instance?.selectRows(newData, false));
      },
    },
    filterData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.applyContextFilterValues();
          this.refreshGrid();
        });
      },
    },
    visible(value) {
      if (value) {
        this.$nextTick(() => {
          this.applyContextFilterValues();
          this.refreshGrid();
          this.$refs.workGrid?.instance?.selectRows(this.localSelectedWorkUuids, false);
        });
      }
    },
  },
  computed: {
    ...mapGetters({ seasons: "seasons/seasons" }),
  },
  async mounted() {
    if (!this.seasons.length) {
      await this.getSeasons({ limit: 1000 });
    }
    this.$nextTick(this.applyContextFilterValues);
  },
  methods: {
    ...mapActions({ getWorks: "seasonOnParcelHasCulture/getSeasonOnParcelHasCulture" }),
    ...mapActions({ getSeasons: "seasons/getSeasons" }),
    refreshGrid() {
      return this.$refs.workGrid?.instance?.refresh();
    },
    onEditorPreparing(e) {
      if (this.lockContextFilters && e.parentType === "filterRow" && ["season.uuid", "work_started_at"].includes(e.dataField)) {
        e.editorOptions.disabled = true;
      }
    },
    applyContextFilterValues() {
      const grid = this.$refs.workGrid?.instance;
      if (!grid || !this.lockContextFilters) return;

      grid.columnOption("season.uuid", "filterValue", this.filterData.season_uuid || null);
      grid.columnOption("work_started_at", "filterValue", null);
    },
    getSeasonColumnText(season_uuid) {
      const season = this.seasons.find((s) => (s.uuid || s.season_uuid) === season_uuid);
      return season ? season.name : "-";
    },
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.localSelectedWorkUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.localSelectedWorkUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.localSelectedWorkUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.workGrid?.instance?.selectRows(this.localSelectedWorkUuids, false);
    },
    onClose() {
      this.$emit("close");
    },
    async onContinue() {
      const selectedRows = this.$refs.workGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected products to the parent component

      this.$refs.workGrid.instance.clearSelection();
      this.localSelectedWorkUuids = [];
      this.$emit("update:selectedWorkUuids", []);
    },
  },
};
</script>

<style lang="scss">
.work-selector-popup .dx-popup-content {
  padding-top: 0 !important;
}
</style>

<style lang="scss" scoped>
.work_selector_grid {
  height: calc(95vh - 290px) !important;
}
</style>
